import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import PageSkeleton from "../components/Skeleton.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { listSuperAgents } from "../workerLine/workerLineApi.js";
import CopyLinkField from "../agent/CopyLinkField.jsx";
import {
  STATUS_FILTERS,
  STATUS_BADGE_VARIANT,
  formatDateTh,
  formatUsage,
  usageNote,
  MetricCard,
  StatusCell,
} from "../agent/inviteCardShared.jsx";
import { PARTNER_COUNTRY_OPTIONS, formatPartnerCountry } from "../public/partnerLetterCountries.js";
import { findOwningSuperAgentOrganizationId, isPlatformSuperAdmin } from "./superAgentOwnership.js";
import {
  createPartnerInviteCard,
  listPartnerApplications,
  listPartnerInviteCards,
  revokePartnerInviteCard,
} from "./partnerInviteCardsApi.js";
import "../agent/agentInviteCards.css";
import "./partnerInviteCards.css";

const SUPER_AGENTS_PATH = "/backoffice/super-agents";

/**
 * The trail has to say how this screen is actually reached: Super Agents > that
 * line's row menu > จดหมายเชิญพันธมิตร. It said "Master Data" while the item was
 * a sibling menu entry; once it was folded into Super Agents that was simply
 * untrue, and it was also the only thing on screen that could have led back.
 *
 * Every crumb goes somewhere real (owner: "ต้องกดได้ทุกอัน"):
 *   Backoffice   -> the dashboard
 *   Super Agents -> the list this screen is opened from
 *   this page    -> itself, keeping ?superAgentId= so the line is not lost
 *
 * The LINE NAME is deliberately not a crumb. It was one, pointing at that line's
 * org-chart page - and the owner caught that this is a place nobody passed
 * through: "ผมกดมาจาก grid ที่ Super Agents". A trail whose middle step leads
 * somewhere the visitor has never been is not a trail, it is a guess about the
 * hierarchy. The line is named on the page itself (the scope pill, or the
 * "ส่งในนามสายงาน" picker), which is where it belongs.
 */
function buildCrumbs(selfPath) {
  return [
    { label: "Backoffice", to: "/backoffice" },
    { label: "Super Agents", to: SUPER_AGENTS_PATH },
    { label: "จดหมายเชิญพันธมิตร", to: selfPath },
  ];
}
const DRAWER_TITLE_ID = "partner-invite-drawer-title";

// WorkerLineAdminService caps a page at 100 - asking for more silently gets 100
// back, so the picker asks for exactly the cap and reports when it hit it.
const LINE_PICKER_PAGE_SIZE = 100;
const LINE_SEARCH_DEBOUNCE_MS = 300;

// The language the letter is written in. Two, and English first: the reader is
// a company abroad and English is the working language of cross-border
// recruitment - Thai is here because the sender wants to read their own letter.
// Mirrors the employer card's own selector mechanism, not its language list.
const PARTNER_LANGUAGE_OPTIONS = [
  { value: "English", label: "English" },
  { value: "Thai", label: "ไทย (Thai)" },
  { value: "Bangla", label: "বাংলা (Bangla)" },
  { value: "Sinhala", label: "සිංහල (Sinhala)" },
];

/** Legacy/blank values still read as a clean label in the table and inspector. */
function formatPartnerLanguage(code) {
  const raw = String(code ?? "").trim().toLowerCase();
  if (!raw) {
    return null;
  }
  if (raw === "en" || raw.startsWith("eng")) {
    return "English";
  }
  if (raw === "th" || raw.startsWith("thai") || raw.startsWith("ไทย")) {
    return "ไทย (Thai)";
  }
  // "bn" is the code the worker invite card has always used for Bangla - a card
  // written before this screen existed still reads correctly here.
  if (raw === "bn" || raw.startsWith("bang") || raw.startsWith("beng")) {
    return "বাংলা (Bangla)";
  }
  if (raw === "si" || raw.startsWith("sinh")) {
    return "සිංහල (Sinhala)";
  }
  return String(code).trim();
}

/**
 * P167. The Super Agent's own Invite Card screen: the letter they send to a
 * licensed recruitment agency abroad, inviting it to join their line as an
 * Agent.
 *
 * Owner: "สร้าง invite card ให้ superagent ใช้เชิญ บริษัท agent ฝั่งแรงงาน เข้าไป
 * agent ครับ ทำรูปแบบที่ agent แรงงานใช้เชิญ แรงงาน เข้าสังกัดเลย" - so this is
 * deliberately the SAME screen as AgentInviteCardsPage one tier up: the same
 * toolbar/metrics/table/pipeline layout, the same wx-ic-* styles, and the same
 * status/usage/expiry helpers (imported from inviteCardShared, not
 * re-implemented). What differs is only what the card is FOR.
 *
 * Two things are genuinely new:
 *  - The letter is per COUNTRY, so the create form picks one. The list can
 *    filter by it.
 *  - The owner is a Super Agent ORGANIZATION, so the screen has to know which
 *    line it is looking at: a Super Agent's own line is resolved from their own
 *    access scope and locked, while a SuperAdmin - who has no single line -
 *    picks one. Job-side (ฝั่งงาน) lines are not offered at all, because a
 *    partner letter recruits a labour-supply agency and the API refuses that
 *    there (P156).
 */
export default function PartnerInviteCardsPage() {
  const { status: identityStatus, data: identity, error: identityError } = useBackofficeIdentity();
  const accessScopes = identity?.accessScopes;
  const ownSuperAgentId = identityStatus === "ready" ? findOwningSuperAgentOrganizationId(accessScopes) : null;
  const canPickAnyLine = identityStatus === "ready" && !ownSuperAgentId && isPlatformSuperAdmin(accessScopes);

  // ?superAgentId= is how the Super Agents row menu names the line it came from
  // (P167 fold). It only pre-selects: the API still decides whether this actor
  // may read or write that line, so an id typed into the URL grants nothing.
  const [searchParams] = useSearchParams();
  const requestedSuperAgentId = searchParams.get("superAgentId");
  // The trail's last crumb links to this exact URL, query string included, so
  // pressing it does not drop the line the screen is showing.
  const location = useLocation();
  const selfPath = `${location.pathname}${location.search}`;

  const [selectedSuperAgentId, setSelectedSuperAgentId] = useState(requestedSuperAgentId);
  const [lines, setLines] = useState({ status: "idle", items: [], totalCount: 0, truncated: false });
  const [requestedLineMissing, setRequestedLineMissing] = useState(false);
  const [lineSearch, setLineSearch] = useState("");

  const [listState, setListState] = useState({ status: "loading", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);
  const [statusFilter, setStatusFilter] = useState("All");
  const [countryFilter, setCountryFilter] = useState("All");
  const [drawer, setDrawer] = useState(null);
  const [applications, setApplications] = useState({ status: "loading", totalCount: 0, items: [] });

  // A Super Agent's own line always wins: ?superAgentId= may not be used to look
  // at somebody else's letters, and the API would refuse it anyway.
  //
  // Nothing is fetched until identity is known. Without that guard the id from
  // the URL is the only candidate during the first render, so a Super Agent
  // opening a link that names another line would fire one request for that line
  // before their own scope had even loaded.
  const superAgentId = identityStatus === "ready" ? ownSuperAgentId ?? selectedSuperAgentId : null;

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);
  const closeDrawer = useCallback(() => setDrawer(null), []);

  // The line picker, for a SuperAdmin only - this is how sysadmin sends a letter
  // on behalf of any Super Agent line ("จดหมายเชิญของ superagent ต้องทำผ่าน
  // sysadmin ได้"). Worker-side lines only: the create endpoint answers 409 on
  // the job side, and offering a choice certain to be refused is worse than not
  // offering it.
  //
  // The list API caps a page at 100 (WorkerLineAdminService.MaxPageSize), so on
  // a platform with more lines than that the picker CANNOT show them all. It
  // says so, and the search box passes a real `search` to the server rather
  // than filtering the truncated page in the browser - filtering a cut-off list
  // is how a line that exists becomes unreachable with no sign anything is
  // missing.
  useEffect(() => {
    if (!canPickAnyLine) {
      return undefined;
    }

    let cancelled = false;
    const trimmedSearch = lineSearch.trim();
    // Debounced so typing a company name is one query, not one per keystroke.
    const timer = window.setTimeout(() => {
      setLines((previous) => ({ ...previous, status: "loading" }));
      listSuperAgents({ page: 1, pageSize: LINE_PICKER_PAGE_SIZE, status: "Active", search: trimmedSearch || undefined })
        .then((data) => {
          if (cancelled) {
            return;
          }
          const items = data?.items ?? [];
          const workerSide = items.filter((item) => item.organizationSide !== "JobDemand");
          setLines({
            status: "ready",
            items: workerSide,
            totalCount: data?.totalCount ?? workerSide.length,
            truncated: items.length >= LINE_PICKER_PAGE_SIZE,
          });
          setSelectedSuperAgentId((current) =>
            workerSide.some((item) => item.superAgentOrganizationId === current)
              ? current
              : workerSide[0]?.superAgentOrganizationId ?? null
          );
          if (requestedSuperAgentId && !workerSide.some((item) => item.superAgentOrganizationId === requestedSuperAgentId)) {
            // The line named in the URL is not in this page of results - say so
            // rather than silently showing a different line's letters.
            setRequestedLineMissing(true);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setLines({ status: "error", items: [], totalCount: 0, truncated: false });
          }
        });
    }, trimmedSearch ? LINE_SEARCH_DEBOUNCE_MS : 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canPickAnyLine, lineSearch]);

  useEffect(() => {
    if (!superAgentId) {
      return undefined;
    }

    let cancelled = false;
    setListState((previous) => ({ status: "loading", data: previous.data, error: null }));
    listPartnerInviteCards(superAgentId, { page: 1, pageSize: 50 })
      .then((data) => {
        if (!cancelled) {
          setListState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setListState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [superAgentId, reloadToken]);

  // How many agencies have actually answered. A real, traceable count read from
  // the server's own totalCount - never the length of the visible page.
  useEffect(() => {
    if (!superAgentId) {
      return undefined;
    }

    let cancelled = false;
    setApplications({ status: "loading", totalCount: 0, items: [] });
    listPartnerApplications(superAgentId, { page: 1, pageSize: 10 })
      .then((data) => {
        if (!cancelled) {
          setApplications({ status: "ready", totalCount: data?.totalCount ?? 0, items: data?.items ?? [] });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setApplications({ status: "error", totalCount: 0, items: [] });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [superAgentId, reloadToken]);

  // A revoked card is history, not something the sender can still act on - the
  // same rule the worker and employer lists apply.
  const items = useMemo(
    () => (listState.data?.items ?? []).filter((card) => card.status !== "Revoked"),
    [listState.data]
  );

  const filteredItems = useMemo(
    () =>
      items.filter(
        (card) =>
          (statusFilter === "All" || card.status === statusFilter) &&
          (countryFilter === "All" || card.partnerCountryCode === countryFilter)
      ),
    [items, statusFilter, countryFilter]
  );

  const metrics = useMemo(
    () => ({
      active: items.filter((card) => card.status === "Active").length,
      expired: items.filter((card) => card.status === "Expired").length,
    }),
    [items]
  );

  const selectedLine = useMemo(
    () => lines.items.find((item) => item.superAgentOrganizationId === superAgentId) ?? null,
    [lines.items, superAgentId]
  );

  const scopeLabel = listState.data?.items?.[0]?.superAgentName ?? selectedLine?.displayName ?? null;
  const scopeCode = listState.data?.items?.[0]?.superAgentCode ?? selectedLine?.superAgentCode ?? null;

  function handleCreated() {
    refresh();
  }

  function handleRevoked() {
    closeDrawer();
    refresh();
  }

  if (identityStatus === "loading") {
    return (
      <AppShell crumbs={buildCrumbs(selfPath)}>
        <PageSkeleton variant="cards" label="กำลังโหลดข้อมูลผู้ใช้งาน..." />
      </AppShell>
    );
  }

  if (identityStatus === "error") {
    return (
      <AppShell crumbs={buildCrumbs(selfPath)}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดข้อมูลผู้ใช้งานได้</h1>
          <p className="wx-state-body">
            {identityError?.status === 401 ? "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่" : "กรุณาลองรีเฟรชหน้านี้อีกครั้ง"}
          </p>
        </div>
      </AppShell>
    );
  }

  if (!ownSuperAgentId && !canPickAnyLine) {
    return (
      <AppShell crumbs={buildCrumbs(selfPath)}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">หน้านี้ใช้ได้เฉพาะ Super Agent เจ้าของสายงาน</h1>
          <p className="wx-state-body">
            บัญชีของคุณไม่ได้ผูกกับสายงาน Super Agent จึงไม่สามารถสร้างจดหมายเชิญบริษัทจัดหางานต่างประเทศได้
          </p>
        </div>
      </AppShell>
    );
  }

  // Without the line list a SuperAdmin has nothing to scope the screen to, and
  // an endless skeleton would read as "still loading" forever. Say what
  // actually happened instead.
  if (canPickAnyLine && lines.status === "error") {
    return (
      <AppShell crumbs={buildCrumbs(selfPath)}>
        <div className="wx-state-card wx-state-card--danger" role="alert">
          <h1 className="wx-state-title">ไม่สามารถโหลดรายชื่อสายงานได้</h1>
          <p className="wx-state-body">กรุณาลองรีเฟรชหน้านี้อีกครั้ง</p>
        </div>
      </AppShell>
    );
  }

  if (canPickAnyLine && lines.status === "ready" && lines.items.length === 0) {
    return (
      <AppShell crumbs={buildCrumbs(selfPath)}>
        <div className="wx-state-card" role="status">
          <h1 className="wx-state-title">ยังไม่มีสายงานฝั่งแรงงานให้เลือก</h1>
          <p className="wx-state-body">
            จดหมายเชิญพันธมิตรใช้เชิญบริษัทจัดหาแรงงาน จึงส่งได้จากสายงานฝั่งแรงงานเท่านั้น
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell crumbs={buildCrumbs(selfPath)}>
      <div className="wx-ic-page">
        <div className="wx-ic-head">
          <div className="wx-ic-title">
            {/* The row menu on Super Agents is how this screen is opened, and a
                pasted URL has no history to go back to - so the way back is on
                the page, not only in the browser's toolbar. */}
            <Link className="wx-pic-back" to={SUPER_AGENTS_PATH}>
              ← กลับไปที่ Super Agents
            </Link>
            <h1>จดหมายเชิญพันธมิตรต่างประเทศ</h1>
            <p>
              สร้างลิงก์จดหมายเชิญให้บริษัทจัดหางานที่มีใบอนุญาตในต่างประเทศเปิดอ่านและกรอกข้อมูลบริษัทเอง
              เมื่อส่งแล้วจะเข้ามารอการตรวจสอบในสายงานของคุณ ยังไม่ถือเป็น Agent จนกว่าคุณจะตรวจสอบและรับเข้าสังกัด
            </p>
          </div>
          <div className="wx-ic-head-actions">
            {canPickAnyLine ? (
              <div className="wx-pic-line-choose">
                <label className="wx-pic-line-picker">
                  <span>ส่งในนามสายงาน</span>
                  <select
                    value={superAgentId ?? ""}
                    onChange={(event) => setSelectedSuperAgentId(event.target.value)}
                    disabled={lines.status !== "ready" || lines.items.length === 0}
                  >
                    {lines.status !== "ready" && <option value="">กำลังโหลด...</option>}
                    {lines.status === "ready" && lines.items.length === 0 && (
                      <option value="">ไม่พบสายงานที่ตรงกับคำค้น</option>
                    )}
                    {lines.items.map((line) => (
                      <option key={line.superAgentOrganizationId} value={line.superAgentOrganizationId}>
                        {line.displayName} ({line.superAgentCode})
                      </option>
                    ))}
                  </select>
                </label>
                <input
                  type="search"
                  className="wx-pic-line-search"
                  value={lineSearch}
                  onChange={(event) => setLineSearch(event.target.value)}
                  placeholder="ค้นหาสายงาน"
                  aria-label="ค้นหาสายงาน"
                />
                {requestedLineMissing && (
                  <span className="wx-pic-line-note">
                    ไม่พบสายงานที่เปิดมาจากลิงก์ในรายการนี้ กำลังแสดงสายงานอื่นแทน
                  </span>
                )}
                {lines.truncated && (
                  <span className="wx-pic-line-note">แสดง {LINE_PICKER_PAGE_SIZE} สายงานแรก ใช้ช่องค้นหาเพื่อหาสายงานอื่น</span>
                )}
              </div>
            ) : (
              <span className="wx-ic-scope-pill">
                Scope <b>{scopeCode ?? "..."}</b>
                {scopeLabel ? ` · ${scopeLabel}` : ""}
              </span>
            )}
            <button
              type="button"
              className="wx-button wx-button--primary wx-ic-create-trigger"
              onClick={() => setDrawer({ mode: "create" })}
              disabled={!superAgentId}
            >
              สร้างจดหมายเชิญ
            </button>
          </div>
        </div>

        {listState.status === "loading" && !listState.data ? (
          <PageSkeleton variant="cards" label="กำลังโหลดรายการจดหมายเชิญ..." />
        ) : listState.status === "error" && !listState.data ? (
          <div className="wx-state-card wx-state-card--danger" role="alert">
            <h1 className="wx-state-title">ไม่สามารถโหลดรายการจดหมายเชิญได้</h1>
            <p className="wx-state-body">กรุณาลองรีเฟรชหน้านี้อีกครั้ง</p>
          </div>
        ) : (
          <>
            <div className="wx-ic-metrics">
              <MetricCard label="ACTIVE INVITES" value={metrics.active} note="ยังส่งต่อให้บริษัทต่างประเทศได้" variant="green" />
              <MetricCard
                label="รอตรวจสอบ"
                value={
                  applications.status === "ready" ? applications.totalCount : applications.status === "error" ? "—" : "..."
                }
                note="บริษัทที่กรอกฟอร์มจากจดหมายของคุณแล้ว"
              />
              <MetricCard label="EXPIRED" value={metrics.expired} note="หมดอายุแล้ว ไม่สามารถใช้งานได้" />
            </div>

            <div className="wx-ic-policy">
              <div>
                <strong>จดหมายเชิญนี้ยังไม่ใช่การรับเข้าสังกัด</strong>
                <span>
                  เมื่อบริษัทกรอกฟอร์ม ระบบจะสร้าง บมจ ใต้สายงานของคุณทันที แต่เป็นสถานะ Inactive
                  จึงยังเพิ่ม Agent ใต้บริษัทนั้นไม่ได้จนกว่าคุณจะตรวจสอบแล้วเปลี่ยนเป็น Active
                </span>
              </div>
              <span className="wx-badge wx-badge--success">Super Agent-only link</span>
            </div>

            <section className="wx-panel wx-ic-list-panel">
              <div className="wx-ic-list-head">
                <h2 className="wx-panel-title">รายการจดหมายเชิญ</h2>
                <div className="wx-ic-filters">
                  {STATUS_FILTERS.map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      className={`wx-ic-chip${statusFilter === filter ? " wx-ic-chip--active" : ""}`}
                      onClick={() => setStatusFilter(filter)}
                    >
                      {filter}
                    </button>
                  ))}
                  <label className="wx-pic-country-filter">
                    <span>ประเทศ</span>
                    <select value={countryFilter} onChange={(event) => setCountryFilter(event.target.value)}>
                      <option value="All">ทั้งหมด</option>
                      {PARTNER_COUNTRY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              {items.length === 0 ? (
                <p className="wx-empty-list">ยังไม่มีจดหมายเชิญที่สร้างจากสายงานนี้</p>
              ) : filteredItems.length === 0 ? (
                <p className="wx-empty-list">ไม่พบจดหมายเชิญที่ตรงกับตัวกรอง</p>
              ) : (
                <div className="wx-ic-table-wrap">
                  <table className="wx-ic-table">
                    <thead>
                      <tr>
                        <th style={{ width: "20%" }}>Invite</th>
                        <th style={{ width: "16%" }}>ประเทศ</th>
                        <th style={{ width: "26%" }}>ข้อความถึงบริษัท</th>
                        <th style={{ width: "14%" }}>Usage</th>
                        <th style={{ width: "16%" }}>Status</th>
                        <th style={{ width: "8%" }} aria-hidden="true" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map((card) => (
                        <tr
                          key={card.partnerInviteCardId}
                          className="wx-ic-row"
                          onClick={() => setDrawer({ mode: "view", card })}
                        >
                          <td>
                            <span className="wx-ic-cell-strong">{card.inviteCodeMasked}</span>
                            <span className="wx-ic-cell-sub">สร้างเมื่อ {formatDateTh(card.createdAtUtc) ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-ic-cell-strong">{formatPartnerCountry(card.partnerCountryCode) ?? "-"}</span>
                            <span className="wx-ic-cell-sub">{formatPartnerLanguage(card.intendedLanguageCode) ?? "-"}</span>
                          </td>
                          <td>
                            <span className="wx-ic-cell-sub">{card.noteToPartner || "-"}</span>
                          </td>
                          <td>
                            <span className="wx-ic-cell-strong">{formatUsage(card)}</span>
                            <span className="wx-ic-cell-sub">{usageNote(card)}</span>
                          </td>
                          <td>
                            <StatusCell card={card} />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="wx-ic-row-action"
                              onClick={(event) => {
                                event.stopPropagation();
                                setDrawer({ mode: "view", card });
                              }}
                            >
                              เปิด
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="wx-ic-pipeline">
                <div className="wx-ic-stage wx-ic-stage--active">
                  <strong>1. ส่งจดหมายเชิญ</strong>
                  <span>Super Agent ส่งลิงก์จดหมายให้บริษัทจัดหางานต่างประเทศ</span>
                </div>
                <div className="wx-ic-stage">
                  <strong>2. บริษัทกรอกข้อมูล</strong>
                  <span>กรอกใบอนุญาต กำลังจัดหาแรงงาน และผู้ติดต่อด้วยตนเอง</span>
                </div>
                <div className="wx-ic-stage">
                  <strong>3. ได้ บมจ อัตโนมัติ</strong>
                  <span>สร้าง บมจ ใต้สายงานทันที สถานะ Inactive ยังเพิ่ม Agent ใต้บริษัทนี้ไม่ได้</span>
                </div>
                <div className="wx-ic-stage">
                  <strong>4. ตรวจสอบแล้วเปิดใช้งาน</strong>
                  <span>ตรวจใบอนุญาต ทำ NDA / MOU แล้วเปลี่ยนสถานะ บมจ เป็น Active</span>
                </div>
              </div>
            </section>

            <PartnerApplicationsPanel applications={applications} superAgentId={superAgentId} />
          </>
        )}
      </div>

      <DetailDrawer
        open={drawer !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-ic-row, .wx-ic-create-trigger"
      >
        {drawer?.mode === "create" && (
          <CreatePartnerInviteCardForm
            superAgentId={superAgentId}
            onCreated={handleCreated}
            onClose={closeDrawer}
            titleId={DRAWER_TITLE_ID}
          />
        )}
        {drawer?.mode === "view" && (
          <PartnerInviteCardInspector
            superAgentId={superAgentId}
            card={drawer.card}
            onRevoked={handleRevoked}
            titleId={DRAWER_TITLE_ID}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}

/**
 * The agencies that answered. Shown here, on the same screen as the letters,
 * because a letter nobody can see the replies to is exactly the gap P156 had to
 * close on the employer side.
 */
function PartnerApplicationsPanel({ applications, superAgentId }) {
  if (applications.status === "loading") {
    return <PageSkeleton variant="table" label="กำลังโหลดรายชื่อบริษัทที่ตอบรับ..." />;
  }

  if (applications.status === "error") {
    return (
      <section className="wx-panel">
        <h2 className="wx-panel-title">บริษัทที่ตอบรับจดหมาย</h2>
        <p className="wx-empty-list">ไม่สามารถโหลดรายชื่อบริษัทที่ตอบรับได้ กรุณาลองรีเฟรชหน้านี้อีกครั้ง</p>
      </section>
    );
  }

  return (
    <section className="wx-panel">
      <div className="wx-ic-list-head">
        <h2 className="wx-panel-title">บริษัทที่ตอบรับจดหมาย</h2>
        <span className="wx-ic-cell-sub">ทั้งหมด {applications.totalCount} ราย</span>
      </div>

      {applications.items.length === 0 ? (
        <p className="wx-empty-list">ยังไม่มีบริษัทกรอกฟอร์มจากจดหมายเชิญของสายงานนี้</p>
      ) : (
        <div className="wx-ic-table-wrap">
          <table className="wx-ic-table">
            <thead>
              <tr>
                <th style={{ width: "24%" }}>บริษัท</th>
                <th style={{ width: "16%" }}>ใบอนุญาต</th>
                <th style={{ width: "20%" }}>บมจ ในสายงาน</th>
                <th style={{ width: "14%" }}>กำลังจัดหาแรงงาน</th>
                <th style={{ width: "18%" }}>ผู้ติดต่อ</th>
                <th style={{ width: "8%" }}>ส่งเมื่อ</th>
              </tr>
            </thead>
            <tbody>
              {applications.items.map((application) => (
                <tr key={application.applicationId}>
                  <td>
                    <span className="wx-ic-cell-strong">{application.companyName}</span>
                    <span className="wx-ic-cell-sub">
                      {[formatPartnerCountry(application.partnerCountryCode), application.officeLocation]
                        .filter(Boolean)
                        .join(" · ") || "-"}
                    </span>
                  </td>
                  <td>
                    <span className="wx-ic-cell-strong">{application.recruitmentLicenseNumber || "-"}</span>
                    <span className="wx-ic-cell-sub">
                      {application.yearEstablished ? `ก่อตั้งปี ${application.yearEstablished}` : "-"}
                    </span>
                  </td>
                  <td>
                    <PartnerCompanyCell application={application} superAgentId={superAgentId} />
                  </td>
                  <td>
                    <span className="wx-ic-cell-sub">{application.workerSupplyCapacity || "-"}</span>
                  </td>
                  <td>
                    <span className="wx-ic-cell-strong">
                      {[application.contactPersonName, application.contactPersonPosition].filter(Boolean).join(" · ") || "-"}
                    </span>
                    <span className="wx-ic-cell-sub">
                      {[application.mobileNumber, application.emailAddress].filter(Boolean).join(" · ") || "-"}
                    </span>
                  </td>
                  <td>
                    <span className="wx-ic-cell-sub">{formatDateTh(application.createdAtUtc) ?? "-"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * The บมจ this application produced under the line. It carries the company's own
 * STATUS because that is the whole review signal: a company created from a form
 * arrives Inactive, and nobody can be placed under it until a human switches it
 * on. An application from before auto-creation has none, and says so rather than
 * showing a link that leads nowhere.
 */
function PartnerCompanyCell({ application, superAgentId }) {
  if (!application.createdPlcOrganizationId) {
    return <span className="wx-ic-cell-sub">ใบสมัครนี้ยื่นก่อนระบบสร้าง บมจ อัตโนมัติ</span>;
  }

  const isActive = application.createdPlcStatus === "Active";
  const name = application.createdPlcName || application.companyName;

  return (
    <>
      {superAgentId ? (
        <Link
          className="wx-pic-company-link"
          to={`/backoffice/super-agents/${encodeURIComponent(superAgentId)}/plcs/${encodeURIComponent(
            application.createdPlcOrganizationId
          )}`}
        >
          {name}
        </Link>
      ) : (
        <span className="wx-ic-cell-strong">{name}</span>
      )}
      <span className={`wx-badge wx-badge--${isActive ? "success" : "warning"}`}>
        {isActive ? "Active" : "Inactive · รอตรวจสอบ"}
      </span>
    </>
  );
}

function describeCreateError(error) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์สร้างจดหมายเชิญของสายงานนี้";
  }
  if (error?.status === 409) {
    return "สายงานนี้เป็นฝั่งงาน (นายจ้าง) จึงไม่สามารถเชิญบริษัทจัดหาแรงงานได้";
  }
  if (error?.status === 400) {
    return error.message?.replace(/^Request failed with status \d+$/, "") || "ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบฟอร์มอีกครั้ง";
  }
  if (error?.status === 500) {
    return "ไม่สามารถสร้างหรือแสดงลิงก์จดหมายเชิญได้ในขณะนี้เนื่องจากปัญหาการตั้งค่าระบบ กรุณาติดต่อฝ่ายปฏิบัติการ";
  }
  return "ไม่สามารถสร้างจดหมายเชิญได้ กรุณาลองใหม่อีกครั้ง";
}

function CreatePartnerInviteCardForm({ superAgentId, onCreated, onClose, titleId }) {
  const [countryCode, setCountryCode] = useState(PARTNER_COUNTRY_OPTIONS[0]?.value ?? "");
  const [languageCode, setLanguageCode] = useState(PARTNER_LANGUAGE_OPTIONS[0].value);
  const [noteToPartner, setNoteToPartner] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [createdCard, setCreatedCard] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const card = await createPartnerInviteCard(superAgentId, {
        partnerCountryCode: countryCode,
        intendedLanguageCode: languageCode || undefined,
        noteToPartner: noteToPartner.trim() || undefined,
      });
      // The full code and URL come back exactly once, on create - the list only
      // ever carries a masked code. Hold it so the sender can copy the link
      // before closing the drawer.
      setCreatedCard(card);
      onCreated();
    } catch (caught) {
      setErrorMessage(describeCreateError(caught));
    } finally {
      setSubmitting(false);
    }
  }

  if (createdCard) {
    return (
      <div>
        <span className="wx-ic-drawer-kicker">CREATE DRAWER</span>
        <h2 id={titleId} className="wx-ic-drawer-title">
          สร้างจดหมายเชิญสำเร็จ
        </h2>
        <p className="wx-ic-drawer-desc">
          ส่งลิงก์นี้ให้บริษัทจัดหางานต่างประเทศเปิดอ่านจดหมายและกรอกข้อมูลบริษัทเอง
          เมื่อส่งแล้วจะเข้ามารอการตรวจสอบในสายงานของคุณ
        </p>
        <div className="wx-ic-preview">
          <strong>Invite URL</strong>
          {createdCard.inviteUrl ? (
            <CopyLinkField url={createdCard.inviteUrl} />
          ) : (
            <span className="wx-ic-preview-url">-</span>
          )}
        </div>
        <p className="wx-ic-field-hint">
          ปิดหน้าต่างนี้แล้วยังกลับมาคัดลอกลิงก์เดิมได้ ที่รายการจดหมายเชิญ กด &quot;เปิด&quot; ที่แถวนั้น
        </p>
        <div className="wx-ic-drawer-foot">
          <button type="button" className="wx-button" onClick={onClose}>
            ปิด
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <span className="wx-ic-drawer-kicker">CREATE DRAWER</span>
      <h2 id={titleId} className="wx-ic-drawer-title">
        สร้างจดหมายเชิญพันธมิตร
      </h2>
      <p className="wx-ic-drawer-desc">บริษัทจะกรอกข้อมูลของตัวเองจากลิงก์นี้ คุณไม่ต้องกรอกแทน</p>

      <div className="wx-ic-form-row wx-ic-form-row--single">
        <div className="wx-ic-field">
          <label htmlFor="partner-invite-country">ประเทศของผู้รับ</label>
          <select
            id="partner-invite-country"
            value={countryCode}
            onChange={(event) => setCountryCode(event.target.value)}
          >
            {PARTNER_COUNTRY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="wx-ic-field-hint">
            เนื้อหาจดหมายและชื่อใบอนุญาตในฟอร์มจะเปลี่ยนตามประเทศที่เลือก
          </span>
        </div>
      </div>

      <div className="wx-ic-form-row wx-ic-form-row--single">
        <div className="wx-ic-field">
          <label htmlFor="partner-invite-language">ภาษาของจดหมาย</label>
          <select
            id="partner-invite-language"
            value={languageCode}
            onChange={(event) => setLanguageCode(event.target.value)}
          >
            {PARTNER_LANGUAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="wx-ic-field-hint">หน้าจดหมายจะเปิดด้วยภาษานี้ก่อน และผู้รับยังสลับภาษาเองได้</span>
        </div>
      </div>

      <div className="wx-ic-form-row wx-ic-form-row--single">
        <div className="wx-ic-field">
          <label htmlFor="partner-invite-note">ข้อความถึงบริษัท (ไม่บังคับ)</label>
          <textarea
            id="partner-invite-note"
            value={noteToPartner}
            onChange={(event) => setNoteToPartner(event.target.value)}
            rows={3}
            maxLength={500}
            placeholder="เช่น เรายินดีอย่างยิ่งหากได้พูดคุยกับทีมงานของท่าน"
          />
          <span className="wx-ic-field-hint">ข้อความนี้จะแสดงในจดหมายเชิญที่บริษัทเปิดอ่าน</span>
        </div>
      </div>

      {errorMessage && (
        <p className="wx-ic-form-message" role="alert">
          {errorMessage}
        </p>
      )}

      <div className="wx-ic-drawer-foot">
        <button type="button" className="wx-button" onClick={onClose}>
          ยกเลิก
        </button>
        <button type="submit" className="wx-button wx-button--primary" disabled={submitting}>
          {submitting ? "กำลังสร้าง..." : "สร้างและคัดลอกลิงก์"}
        </button>
      </div>
    </form>
  );
}

function PartnerInviteCardInspector({ superAgentId, card, onRevoked, titleId }) {
  const [revoking, setRevoking] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const variant = STATUS_BADGE_VARIANT[card.status] ?? "neutral";

  async function handleRevoke() {
    setRevoking(true);
    setErrorMessage(null);
    try {
      await revokePartnerInviteCard(superAgentId, card.partnerInviteCardId);
      onRevoked();
    } catch (caught) {
      setErrorMessage(caught?.message ?? "ไม่สามารถเพิกถอนลิงก์นี้ได้ กรุณาลองใหม่อีกครั้ง");
    } finally {
      setRevoking(false);
    }
  }

  return (
    <div>
      <div className="wx-ic-inspector-head">
        <div>
          <span className={`wx-badge wx-badge--${variant}`}>{card.status}</span>
          <h2 id={titleId} className="wx-ic-drawer-title">
            จดหมายเชิญพันธมิตร
          </h2>
          <div className="wx-ic-inspector-code">{card.inviteCodeMasked}</div>
        </div>
      </div>

      {card.status === "Revoked" ? (
        <p className="wx-ic-inspector-note">ลิงก์นี้ถูกเพิกถอนแล้ว ไม่สามารถใช้งานได้อีก แต่ยังเก็บไว้เป็น audit history</p>
      ) : card.inviteUrl ? (
        <div className="wx-ic-preview">
          <strong>Invite URL</strong>
          <CopyLinkField url={card.inviteUrl} />
        </div>
      ) : (
        // Unlike the worker and employer cards, this API returns the full link
        // on every read, so this branch is not the normal case - it only shows
        // if the server stopped sending it, and then the honest thing is to say
        // the link cannot be shown right now, not that it is gone forever.
        <p className="wx-ic-inspector-note">ตอนนี้ยังแสดงลิงก์เต็มไม่ได้ กรุณาลองรีเฟรชหน้านี้อีกครั้ง</p>
      )}

      <div className="wx-kv-grid wx-ic-inspector-kv">
        <div>
          <div className="wx-kv-label">ประเทศของผู้รับ</div>
          <div className="wx-kv-value">{formatPartnerCountry(card.partnerCountryCode) ?? "-"}</div>
        </div>
        <div>
          <div className="wx-kv-label">ภาษาของจดหมาย</div>
          <div className="wx-kv-value">{formatPartnerLanguage(card.intendedLanguageCode) || "-"}</div>
        </div>
        <div>
          <div className="wx-kv-label">การใช้งาน</div>
          <div className="wx-kv-value">{formatUsage(card)}</div>
        </div>
        <div>
          <div className="wx-kv-label">หมดอายุ</div>
          <div className="wx-kv-value">{formatDateTh(card.expiresAtUtc) ?? "ไม่มีวันหมดอายุ"}</div>
        </div>
        <div>
          <div className="wx-kv-label">สร้างเมื่อ</div>
          <div className="wx-kv-value">{formatDateTh(card.createdAtUtc) ?? "-"}</div>
        </div>
        {card.status === "Revoked" && (
          <div>
            <div className="wx-kv-label">เพิกถอนเมื่อ</div>
            <div className="wx-kv-value">{formatDateTh(card.revokedAtUtc) ?? "-"}</div>
          </div>
        )}
        <div>
          <div className="wx-kv-label">ข้อความถึงบริษัท</div>
          <div className="wx-kv-value">{card.noteToPartner || "-"}</div>
        </div>
      </div>

      {errorMessage && (
        <p className="wx-ic-form-message" role="alert">
          {errorMessage}
        </p>
      )}

      {card.status !== "Revoked" && (
        <div className="wx-ic-inspector-actions">
          <button type="button" className="wx-button wx-button--ghost" onClick={handleRevoke} disabled={revoking}>
            {revoking ? "กำลังเพิกถอน..." : "เพิกถอนจดหมายเชิญ"}
          </button>
          <p className="wx-ic-inspector-revoke-note">
            เพิกถอนเฉพาะลิงก์ใหม่เท่านั้น ข้อมูลบริษัทที่ส่งเข้ามาแล้วจะยังอยู่ในรายชื่อผู้สมัคร ไม่มีการลบข้อมูล
          </p>
        </div>
      )}
    </div>
  );
}
